import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/api-error';
import type { Permission } from '@/shared/auth/permissions';

import { getCurrentUser, login as loginRequest, logout as logoutRequest } from '../api/auth.api';
import { currentUserKeys, useCurrentUser } from '../api/current-user.queries';
import { getRefreshToken, useAuthStore } from '../store/auth.store';
import type { CountryScope, LoginCredentials } from '../types/auth.types';

/** The permission that decides whether an account may use this application at all. */
export const ADMIN_ACCESS: Permission = 'admin.access';

/**
 * The application's view of the current session.
 *
 * Components use this rather than touching the store or the query directly, so
 * that where each piece lives stays an implementation detail of the auth
 * feature (§16).
 */
export function useAuth() {
  const status = useAuthStore((state) => state.status);
  const endReason = useAuthStore((state) => state.endReason);
  const currentUser = useCurrentUser();

  return {
    status,
    isAuthenticated: status === 'authenticated',
    isRestoring: status === 'restoring',
    /** Why the previous session ended, when it was not an explicit sign-out. */
    endReason,
    /** The signed-in user from `/admin/me`; undefined until it has loaded. */
    user: currentUser.data,
    isUserLoading: currentUser.isPending && status === 'authenticated',
    userError: currentUser.error,
    refetchUser: currentUser.refetch,
  };
}

/**
 * Permission checks, backed by the permissions `/admin/me` returned.
 *
 * `can('orders.read')` is the only sanctioned way to branch on authority.
 * Never compare roles — `user.role === 'Admin'` scattered through components is
 * exactly what this exists to prevent (§17). Until `/me` has loaded, every
 * check is false.
 */
export function usePermissions() {
  const { data } = useCurrentUser();
  const permissions = data?.permissions;

  const can = useCallback(
    (permission: Permission) => permissions?.includes(permission) ?? false,
    [permissions],
  );

  const canAny = useCallback(
    (required: readonly Permission[]) => required.some((permission) => can(permission)),
    [can],
  );

  const canAll = useCallback(
    (required: readonly Permission[]) => required.every((permission) => can(permission)),
    [can],
  );

  return { can, canAny, canAll, permissions: permissions ?? [] };
}

/**
 * Sign in against the real backend.
 *
 * login → /admin/me (with the new token) → admin.access? The session is only
 * stored once the account is known to have admin access, so a customer or
 * reseller account never reaches the shell, not even for a frame. A rejected
 * account's refresh token is revoked immediately.
 */
/** Stable fallback: no country until `/admin/me` has loaded. */
const NO_COUNTRY_SCOPE: CountryScope = { allCountries: false, countries: [] };

/**
 * The countries the signed-in staff member may work in, from `/admin/me`.
 * Drives the country selector; the Admin API re-checks every country anyway.
 */
export function useCountryScope(): CountryScope {
  const { data } = useCurrentUser();
  return data?.countryScope ?? NO_COUNTRY_SCOPE;
}

export function useLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const session = await loginRequest(credentials);
      const user = await getCurrentUser(session.accessToken);

      if (!user.permissions.includes(ADMIN_ACCESS)) {
        await logoutRequest(session.refreshToken);
        throw new ApiError({
          message: 'This account has no access to the admin.',
          kind: 'http',
          status: 403,
          code: 'NO_ADMIN_ACCESS',
        });
      }

      return { session, user };
    },
    onSuccess: ({ session, user }) => {
      // Seed the query before storing the session, so the guards find the user
      // already loaded on the first render after sign-in.
      queryClient.setQueryData(currentUserKeys.me, user);
      setSession(session);
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useCallback(() => {
    // Revoke server-side without waiting: signing out must not hang on the network.
    void logoutRequest(getRefreshToken());
    clear();
    // Wipe cached data so the next user of this browser cannot read the
    // previous session's data out of the query cache.
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [clear, queryClient, navigate]);
}
